/* eslint-disable  @typescript-eslint/no-explicit-any */
import { TimezoneDetails } from '@comet-ui-components/comet-header-component';
import { COLUMN_DEFINITIONS, Environment } from '../constants';
import moment from 'moment-timezone';
import { GlobalSearchEnv } from '@comet-mis-traceability/global-search';
import type {
  Attribute,
  EntityTypeAttributes,
  AttributeProperty,
} from '../models/EditableList';
import type { EntityType } from '../models/EntityType';
import { startCase } from 'lodash';
import { getAttributeDisplayName } from './bpsMapping';

/**
 * Provides the default time zone.
 */
export const getDefaultTimezone = (): TimezoneDetails => {
  const currentTimezone = moment.tz.guess(); // Guesses the user's timezone based on their system
  if (currentTimezone) {
    const now = moment.tz(currentTimezone);
    const offset = now.utcOffset();
    const offsetHours = Math.floor(Math.abs(offset) / 60);
    const offsetMinutes = Math.abs(offset) % 60;
    const offsetSign = offset >= 0 ? '+' : '-';
    const formattedOffset = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;

    return {
      label: `UTC (${formattedOffset})`,
      abbreviation: now.format('z'), // Gets the timezone abbreviation (e.g., EST, IST)
      value: currentTimezone,
    };
  } else {
    return {
      label: 'UTC (+00:00)',
      abbreviation: 'UTC',
      value: 'UTC',
    };
  }
};
/**
 * Common function to jump to redirect URL
 * @param redirectUrl
 */
export const jumpToRedirectUrl = (redirectUrl: string) => {
  window.open(redirectUrl, '_blank');
};

/**
 * Function to return relative time from now given a datetime object or string or epoch time
 * @param dateTime - datetime string or Date or epoch time
 * @returns - relative time string
 */

export const formatRelativeTime = (
  timestamp: string | number | Date,
): string => {
  const diffMs = Date.now() - new Date(timestamp).getTime();

  if (Number.isNaN(diffMs)) {
    return '---';
  }

  if (diffMs < 0) {
    return '< 1 min ago';
  }

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);

  if (totalMinutes < 1) {
    return '< 1 min ago';
  }

  if (totalMinutes < 60) {
    return `${totalMinutes} min${totalMinutes === 1 ? '' : 's'} ago`;
  }

  if (totalHours < 24) {
    const minutes = totalMinutes % 60;

    return minutes > 0
      ? `${totalHours} hr${totalHours === 1 ? '' : 's'} ${minutes} min${minutes === 1 ? '' : 's'} ago`
      : `${totalHours} hr${totalHours === 1 ? '' : 's'} ago`;
  }

  if (totalDays < 7) {
    const hours = totalHours % 24;

    return hours > 0
      ? `${totalDays} day${totalDays === 1 ? '' : 's'} ${hours} hr${hours === 1 ? '' : 's'} ago`
      : `${totalDays} day${totalDays === 1 ? '' : 's'} ago`;
  }

  if (totalDays < 365) {
    return `${totalDays} day${totalDays === 1 ? '' : 's'} ago`;
  }

  const years = Math.floor(totalDays / 365);

  return `${years} year${years === 1 ? '' : 's'} ago`;
};

/**
 * converts camelCase string to Title Case
 * @param str - string input
 * @returns - title cased string
 */
export const toTitleCase = (str: string | undefined): string => {
  return (
    (str ?? '')
      // Insert space before capital letter that is followed by a lowercase letter
      .replace(/([a-z])([A-Z][a-z])/g, '$1 $2')
      // Insert space between a lowercase and uppercase character (basic case)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // Capitalize each word (preserve acronyms)
      .split(' ')
      .map((word) => {
        if (word.toUpperCase() === word) {
          // Word is an acronym like API or HTML
          return word;
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ')
  );
};

/**
 * Singularizes words whose plurals end in suffix '-s', when count === 1.
 * Is enough for entity types currently.
 * For a more robust implementation, should use `npm install pluralize`
 * @param label - input string
 * @param count - count for making decision on singularizing
 * @returns - singularized label
 */
export const singularize = (label: string, count: number): string => {
  return count === 1 ? label.slice(0, -1) : label;
};

/**
 * Sorts items in array based on columns and order. Used in tabular data
 * @param items - array items which are input to table
 * @param setItems - function to set sorted items, used for state management
 * @param columnId - name of table column to sort by
 * @param desc - sort order
 */
export function handleSort<T extends HasTimeMetadata>(
  items: T[],
  setItems: (data: T[]) => void,
  columnId: string,
  desc: boolean,
): void {
  let sortedData: any[] = [];
  switch (columnId) {
    case COLUMN_DEFINITIONS.LATEST_UPDATE:
      sortedData = items.sort((a, b) => {
        const aTime = a.updatedTimestamp ?? a.createdTimestamp;
        const bTime = b.updatedTimestamp ?? b.createdTimestamp;
        const result = aTime < bTime ? -1 : aTime === bTime ? 0 : 1; // forcing type to number
        return desc ? -result : result;
      });
      setItems(sortedData);
      break;
    default:
      sortedData = items.sort((a, b) => {
        const result = a[columnId].localeCompare(b[columnId]);
        return desc ? -result : result;
      });
  }
}

interface HasTimeMetadata {
  updatedTimestamp?: Date;
  createdTimestamp: Date;
  [key: string]: any | undefined;
}

/**
 * Get Environment Specific to Global Search
 * @returns {GlobalSearchEnv}
 */
export const getGlobalSearchEnv = (): GlobalSearchEnv => {
  switch (import.meta.env.VITE_APP_ENV) {
    case Environment.PRODUCTION:
      return GlobalSearchEnv.PRODUCTION;
    case Environment.STAGING:
      return GlobalSearchEnv.STAGING;
    default:
      return GlobalSearchEnv.DEVELOPMENT;
  }
};

/**
 * Generate unique ID with a given prefix
 * @param prefix - prefix for the generated ID
 * @returns - unique ID string
 */
export const generateId = (prefix: string): string =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

/**
 * Convert Attribute[] to EntityTypeAttributes format for API
 * @param attributes - array of Attribute objects
 * @returns - EntityTypeAttributes object or null if empty
 */
export const convertToEntityTypeAttributes = (
  attributes: Attribute[],
): EntityTypeAttributes | null => {
  if (attributes.length === 0) return null;

  const buildProperties = (
    attrs: Attribute[],
  ): Record<string, AttributeProperty> => {
    const properties: Record<string, AttributeProperty> = {};

    attrs.forEach((attr) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { name, children, id, ...schemaFields } = attr;

      const prop: AttributeProperty = {
        ...schemaFields, // type ,preserves format, minLength, etc.
      };

      if (children?.length) {
        if (attr.type === 'array') {
          // Arrays use 'items' to define the schema of array elements
          prop.items = {
            type: 'object',
            properties: buildProperties(children),
          };
        } else if (attr.type === 'object') {
          // Objects use 'properties' directly
          prop.properties = buildProperties(children);
        }
      }

      properties[name] = prop;
    });

    return properties;
  };

  return {
    additionalProperties: false,
    type: 'object',
    properties: buildProperties(attributes),
  };
};

/**
 * Convert EntityTypeAttributes format from API to Attribute[] format
 * @param entityTypeAttributes - EntityTypeAttributes object from API
 * @param transformNames - whether to transform attribute names for display (default: true)
 * @returns - array of Attribute objects
 */
export const convertFromEntityTypeAttributes = (
  entityTypeAttributes: EntityType['entityTypeAttributes'],
  transformNames: boolean = true,
): Attribute[] => {
  if (!entityTypeAttributes?.properties) return [];

  let idCounter = 0;

  const buildAttributes = (
    properties: Record<string, AttributeProperty>,
  ): Attribute[] => {
    return Object.entries(properties).map(([name, prop]) => {
      const { properties: childProps, items, ...rest } = prop;

      const attribute: Attribute = {
        id: generateId(`attr-${idCounter++}`),
        name: transformNames ? getAttributeDisplayName(name) : name,
        ...rest,
      };

      if (prop.type === 'object' && childProps) {
        attribute.children = buildAttributes(childProps);
      }

      if (prop.type === 'array' && items) {
        if (items.properties) {
          attribute.children = buildAttributes(items.properties);
        } else {
          attribute.items = items;
        }
      }

      return attribute;
    });
  };

  return buildAttributes(
    entityTypeAttributes.properties as Record<string, AttributeProperty>,
  );
};

/**
 * Transform attribute names to display names (recursive)
 * @param attributes - array of Attribute objects with original keys
 * @returns - array of Attribute objects with display names
 */
export const transformAttributesToDisplay = (
  attributes: Attribute[],
): Attribute[] => {
  return attributes.map((attr) => ({
    ...attr,
    name: getAttributeDisplayName(attr.name),
    children: attr.children
      ? transformAttributesToDisplay(attr.children)
      : undefined,
  }));
};

/**
 * Validates a field value for required and uniqueness
 * @param value - the value to validate
 * @param fieldLabel - label for error messages
 * @param existingNames - array of existing names (lowercase) to check against
 * @param currentName - current name to exclude from uniqueness check (for edit mode)
 * @returns error message or null if valid
 */
export const validateField = (
  value: string,
  fieldLabel: string,
  existingNames: string[],
  currentName?: string,
): string | null => {
  if (!value.trim()) {
    return `${fieldLabel} is required`;
  }
  const namesToCheck = currentName
    ? existingNames.filter((n) => n !== currentName.toLowerCase())
    : existingNames;
  if (namesToCheck.includes(value.trim().toLowerCase())) {
    return `${fieldLabel} already exists`;
  }
  return null;
};

/**
 * Helper to get style class with fallback from a styles object
 * @param styles - CSS module styles object
 * @param prefix - prefix for the class name
 * @param suffix - suffix for the class name
 * @param fallback - fallback class name if the combined class doesn't exist
 * @returns the resolved style class name
 */
export const getStyleClass = (
  styles: Record<string, string>,
  prefix: string,
  suffix: string,
  fallback: string,
): string => styles[`${prefix}${suffix}`] || styles[fallback];

/**
 * Convert camelCase or PascalCase text to Start Case
 * @example "helloWorld" -> "Hello World"
 * @example "entityTypeId" -> "Entity Type Id"
 */
export const toStartCase = (text: string): string => {
  return startCase(text);
};

export const toCamelCase = (str: string) =>
  str
    .trim()
    .replace(/\s+(.)/g, (_, c) => c.toUpperCase())
    .replace(/\s/g, '')
    .replace(/^(.)/, (c) => c.toLowerCase());

export const toDisplayName = (str: string) =>
  getAttributeDisplayName(str)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (s) => s.toUpperCase());
